import type { FieldId } from "../domain/types.js";
import type { CampaignAuthoredFieldSource, CampaignMapConfig } from "./types.js";

const COLS = "ABCDEFGHIJKLM";
const ROWS = 13;

const fieldIds: FieldId[] = [];
for (let r = 0; r < ROWS; r++) {
  for (let c = 0; c < COLS.length; c++) {
    fieldIds.push(`${COLS[c]}${ROWS - r}` as FieldId);
  }
}

// row-major, row=0 top, col=0 left; false = ocean / decorative border.
const LAND_MASK: readonly boolean[] = [
  false, false, false, false, false, false, false, false, false, false, false, false, false,
  false, false, false, false, false, false, false, false, false, false, false, false, false,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  false, false,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  false, true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  false, true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  false, false, true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  false, false, false, true,  true,  true,  true,  true,  true,  true,  true,  true,  true,
  false, false, false, false, true,  true,  true,  true,  true,  true,  true,  true,  true,
];

export const emptyAuthoredFields: CampaignAuthoredFieldSource = {
  get() {
    return null;
  },
};

export function chainAuthoredFields(
  ...sources: CampaignAuthoredFieldSource[]
): CampaignAuthoredFieldSource {
  return {
    get(fieldId: FieldId) {
      for (const source of sources) {
        const field = source.get(fieldId);
        if (field) return field;
      }
      return null;
    },
  };
}

export interface SharedGridMapOptions {
  authoredFields?: CampaignAuthoredFieldSource;
  landMask?: readonly boolean[];
  startingFieldId?: FieldId;
  finalFieldId?: FieldId;
}

function fieldIdToCoords(fieldId: FieldId): { row: number; col: number } | null {
  const col = fieldId.charCodeAt(0) - 65;
  const num = parseInt(fieldId.slice(1), 10);
  if (col < 0 || col >= COLS.length || !Number.isFinite(num) || num < 1 || num > ROWS) {
    return null;
  }
  return { row: ROWS - num, col };
}

export function createSharedGridMap(
  authoredFieldsOrOptions: CampaignAuthoredFieldSource | SharedGridMapOptions = emptyAuthoredFields,
): CampaignMapConfig {
  const options: SharedGridMapOptions = "get" in authoredFieldsOrOptions
    ? { authoredFields: authoredFieldsOrOptions }
    : authoredFieldsOrOptions;
  const authoredFields = options.authoredFields ?? emptyAuthoredFields;
  const landMask = options.landMask ?? LAND_MASK;
  if (landMask.length !== ROWS * COLS.length) {
    throw new Error(`Shared grid land mask must have ${ROWS * COLS.length} entries`);
  }

  function isFieldPlayable(fieldId: FieldId): boolean {
    const coords = fieldIdToCoords(fieldId);
    if (!coords) return false;
    return landMask[coords.row * COLS.length + coords.col] === true;
  }

  function getAdjacentFieldIds(fieldId: FieldId): FieldId[] {
    const coords = fieldIdToCoords(fieldId);
    if (!coords) return [];
    const neighbors: FieldId[] = [];
    const candidates = [
      { row: coords.row - 1, col: coords.col },
      { row: coords.row + 1, col: coords.col },
      { row: coords.row, col: coords.col - 1 },
      { row: coords.row, col: coords.col + 1 },
    ];
    for (const cell of candidates) {
      if (cell.row < 0 || cell.row >= ROWS || cell.col < 0 || cell.col >= COLS.length) continue;
      const id = fieldIds[cell.row * COLS.length + cell.col];
      if (isFieldPlayable(id)) neighbors.push(id);
    }
    return neighbors;
  }

  return {
    rows: ROWS,
    cols: COLS.length,
    fieldIds,
    startingFieldId: options.startingFieldId ?? "G1",
    finalFieldId: options.finalFieldId ?? "G7",
    authoredFields,
    fieldIdToCoords,
    isFieldPlayable,
    getAdjacentFieldIds,
  };
}

export function createWheelMandalaLandMask(): readonly boolean[] {
  const centerRow = 6;
  const centerCol = 6;
  const mask: boolean[] = [];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS.length; col++) {
      const dr = row - centerRow;
      const dc = col - centerCol;
      const dist = Math.hypot(dr, dc);
      const onCardinalSpoke = row === centerRow || col === centerCol;
      const onDiagonalSpoke = Math.abs(dr) === Math.abs(dc);
      const onInnerHub = dist <= 1.45;
      const onOuterRing = dist >= 4.35 && dist <= 5.55;
      const onSpoke = (onCardinalSpoke || onDiagonalSpoke) && dist <= 5.55;
      mask.push(onInnerHub || onOuterRing || onSpoke);
    }
  }
  mask[(ROWS - 1) * COLS.length + 6] = true; // G1
  mask[centerRow * COLS.length + centerCol] = true; // G7
  return mask;
}
